import { useState } from "react";
import { Bell, CheckCheck, ChevronLeft, ChevronRight, Inbox, Loader2 } from "lucide-react";

import { NotificationItem } from "@/components/NotificationBell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from "@/hooks/useNotifications";

const PAGE_SIZE = 20;

/** Full-page /notifications view — toggle between all / unread,
 *  paginated, click a row to mark-read + open its link. */
export default function Notifications() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page,       setPage]       = useState(1);

  const list   = useNotifications({ unreadOnly, page });
  const unread = useUnreadCount();
  const markRead = useMarkRead();
  const markAll  = useMarkAllRead();

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Notifications</h1>
            <p className="text-sm text-muted-foreground">
              {unread.data && unread.data.unread > 0
                ? `${unread.data.unread} unread`
                : "All caught up."}
            </p>
          </div>
        </div>

        {unread.data && unread.data.unread > 0 && (
          <Button
            variant="outline" size="sm"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {markAll.isPending
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <CheckCheck className="mr-2 h-4 w-4" />}
            Mark all as read
          </Button>
        )}
      </header>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={unreadOnly ? "ghost" : "secondary"}
          size="sm"
          onClick={() => { setUnreadOnly(false); setPage(1); }}
        >
          All
        </Button>
        <Button
          variant={unreadOnly ? "secondary" : "ghost"}
          size="sm"
          onClick={() => { setUnreadOnly(true); setPage(1); }}
        >
          Unread {unread.data && unread.data.unread > 0 && (
            <Badge variant="outline" className="ml-2 font-mono tabular-nums">
              {unread.data.unread}
            </Badge>
          )}
        </Button>
      </div>

      {/* List */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : list.data && list.data.results.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {list.data.results.map((n) => (
                <li key={n.id}>
                  <NotificationItem
                    n={n}
                    dense
                    onClick={() => { if (!n.is_read) markRead.mutate(n.id); }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-12 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold">
                {unreadOnly ? "Nothing unread" : "No notifications yet"}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {unreadOnly
                  ? "Switch to All to see what you've already read."
                  : "We'll buzz here when something needs you."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {list.data && list.data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold tabular-nums">
              {list.data.count.toLocaleString()}
            </span>{" "}
            notification{list.data.count === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              disabled={!list.data?.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />Previous
            </Button>
            <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
              {page} / {totalPages}
            </div>
            <Button
              variant="outline" size="sm"
              disabled={!list.data?.next}
              onClick={() => setPage((p) => p + 1)}
            >
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
